import { z } from 'zod';
import { CallModeSchema } from './booking';

/**
 * Voice/video contracts (Module 7, Step 1). Backs the `CallRecord` model
 * and the LiveKit room-token exchange. `callMode` on the response always
 * comes from the Booking, never from the request — see voice.service.ts.
 */

export const CallStatusSchema = z.enum(['INITIALIZING', 'ACTIVE', 'ENDED', 'FAILED']);
export type CallStatus = z.infer<typeof CallStatusSchema>;

export const CallQualitySchema = z.enum(['GOOD', 'POOR', 'UNKNOWN']);
export type CallQuality = z.infer<typeof CallQualitySchema>;

/** POST /api/v1/voice/token */
export const VoiceTokenRequestSchema = z.object({
  bookingId: z.string().cuid(),
});
export type VoiceTokenRequest = z.infer<typeof VoiceTokenRequestSchema>;

export const VoiceTokenResponseSchema = z.object({
  token: z.string(),
  url: z.string(),
  roomName: z.string(),
  callMode: CallModeSchema,
  identity: z.string(),
});
export type VoiceTokenResponse = z.infer<typeof VoiceTokenResponseSchema>;

/** POST /api/v1/voice/end */
export const VoiceEndRequestSchema = z.object({
  bookingId: z.string().cuid(),
  quality: CallQualitySchema.optional(),
});
export type VoiceEndRequest = z.infer<typeof VoiceEndRequestSchema>;

export const VoiceEndResponseSchema = z.object({
  callRecordId: z.string().cuid(),
  status: CallStatusSchema,
  durationSeconds: z.number().int().nonnegative().nullable(),
});
export type VoiceEndResponse = z.infer<typeof VoiceEndResponseSchema>;

/**
 * POST /api/v1/voice/webhook (Module 7 Step 2). Called by LiveKit itself, not
 * by our own clients — there's no request schema because the body is a raw
 * signed string that voice.service.ts hands to livekit-server-sdk's
 * WebhookReceiver, not something we parse with Zod first.
 */
export const VoiceWebhookResponseSchema = z.object({ ok: z.boolean() });
export type VoiceWebhookResponse = z.infer<typeof VoiceWebhookResponseSchema>;
